import { Injectable } from '@angular/core';

export interface StripePaymentMethodResult {
	paymentMethodId: string;
	error?: string;
}

/** Stripe expects ISO 3166-1 alpha-2 when country is sent with postal_code. */
export interface StripeBillingAddress {
	line1?: string;
	line2?: string;
	city?: string;
	state?: string;
	postal_code?: string;
	country?: string;
}

function pickAddrPart(v: unknown): string | undefined {
	if (v == null) return undefined;
	const s = String(v).trim();
	return s !== '' ? s : undefined;
}

function inferCountryIsoFromPostal(postal: string): string | undefined {
	const compact = postal.replace(/\s/g, '').toUpperCase();
	if (/^\d{5}(-\d{4})?$/.test(compact)) {
		return 'US';
	}
	if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(compact)) {
		return 'CA';
	}
	return undefined;
}

function countryHintToIso(raw: string | undefined): string | undefined {
	if (!raw) return undefined;
	const u = raw.trim().toUpperCase();
	if (u.length === 2 && /^[A-Z]{2}$/.test(u)) {
		return u;
	}
	if (u === 'US' || u === 'USA' || u.includes('UNITED STATES')) {
		return 'US';
	}
	if (u === 'CA' || u === 'CAN' || u.includes('CANADA')) {
		return 'CA';
	}
	return undefined;
}

/**
 * Billing address for PaymentMethod from ship-to (branch) or customer bill-to fields.
 * Used when Card Element postal is hidden so CA/US postal formats are not mis-validated in the iframe.
 */
export function buildStripeBillingAddressFromCustomerContext(branch: any, customer: any): StripeBillingAddress | undefined {
	const line1 =
		pickAddrPart(branch?.braddress1) ??
		pickAddrPart(customer?.braddress1) ??
		pickAddrPart(customer?.address1);
	const line2 =
		pickAddrPart(branch?.braddress2) ??
		pickAddrPart(customer?.braddress2) ??
		pickAddrPart(customer?.address2);
	const city =
		pickAddrPart(branch?.braddress3) ??
		pickAddrPart(customer?.address3);
	const state =
		pickAddrPart(branch?.braddress4) ??
		pickAddrPart(customer?.address4);
	const postal =
		pickAddrPart(branch?.braddress5) ??
		pickAddrPart(customer?.address5);
	const countryFromField = countryHintToIso(
		pickAddrPart(branch?.braddress6) ?? pickAddrPart(customer?.address6)
	);
	const country = countryFromField ?? (postal ? inferCountryIsoFromPostal(postal) : undefined);

	const addr: StripeBillingAddress = {};
	if (line1) addr.line1 = line1;
	if (line2) addr.line2 = line2;
	if (city) addr.city = city;
	if (state) addr.state = state;
	if (postal) addr.postal_code = postal;
	if (country) addr.country = country;

	return Object.keys(addr).length ? addr : undefined;
}

/** Defaults tuned to match Angular Material outline fields (Roboto, 16px, neutral grays). */
const DEFAULT_ELEMENTS_APPEARANCE = {
	theme: 'stripe' as const,
	variables: {
		fontFamily: 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
		fontSizeBase: '16px',
		borderRadius: '4px',
		colorBackground: '#ffffff',
		// Appearance API accepts HEX or rgb()/hsl() — not rgba() (Stripe.js validates variables).
		colorText: '#212121',
		colorTextSecondary: '#757575',
		colorTextPlaceholder: '#9e9e9e',
		colorDanger: '#f44336',
		colorPrimary: '#3f51b5',
		spacingUnit: '8px',
	},
};

const DEFAULT_CARD_STYLE = {
	base: {
		fontSize: '16px',
		color: '#212121',
		fontFamily: 'Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
		fontSmoothing: 'antialiased',
		'::placeholder': { color: '#9e9e9e' },
		iconColor: '#757575',
	},
	invalid: {
		color: '#f44336',
		iconColor: '#f44336',
	},
};

@Injectable({
	providedIn: 'root'
})
export class StripeService {
	private stripe: any = null;
	private elements: any = null;
	private cardElement: any = null;
	private currentKey: string = '';

	async init(publishableKey: string): Promise<boolean> {
		if (!publishableKey || (this.stripe && this.currentKey === publishableKey)) {
			return !!this.stripe;
		}
		this.currentKey = publishableKey;
		try {
			const loadStripe = (await import('@stripe/stripe-js')).loadStripe;
			this.stripe = await loadStripe(publishableKey);
			this.elements = null;
			this.cardElement = null;
			return !!this.stripe;
		} catch {
			return false;
		}
	}

	isReady(): boolean {
		return !!this.stripe;
	}

	canCreatePaymentMethod(): boolean {
		return !!(this.stripe && this.cardElement);
	}

	mountCardElement(
		container: HTMLElement,
		options?: { style?: object; appearance?: object; hidePostalCode?: boolean }
	): Promise<boolean> {
		if (!this.stripe || !container) {
			return Promise.resolve(false);
		}
		if (this.cardElement) {
			this.cardElement.unmount();
			this.cardElement = null;
		}
		if (!this.elements) {
			this.elements = this.stripe.elements({
				appearance: (options?.appearance ?? DEFAULT_ELEMENTS_APPEARANCE) as any,
			});
		}
		const hidePostalCode = options?.hidePostalCode !== false;
		this.cardElement = this.elements.create('card', {
			style: (options?.style ?? DEFAULT_CARD_STYLE) as any,
			hidePostalCode,
		});
		this.cardElement.mount(container);
		return Promise.resolve(true);
	}

	unmountCardElement(): void {
		if (this.cardElement) {
			this.cardElement.unmount();
			this.cardElement = null;
		}
		// Fresh `elements` on next mount avoids Stripe Card Element flakiness after unmount/reopen cycles.
		this.elements = null;
	}

	async createPaymentMethod(billingName?: string, billingAddress?: StripeBillingAddress): Promise<StripePaymentMethodResult> {
		if (!this.stripe || !this.cardElement) {
			return {
				paymentMethodId: '',
				error: 'Secure card fields did not load. Please refresh the page and try again, or contact support if it persists.'
			};
		}
		try {
			const payload: any = { type: 'card', card: this.cardElement };
			const billing_details: any = {};
			if (billingName) {
				billing_details.name = billingName;
			}
			if (billingAddress && Object.keys(billingAddress).length) {
				billing_details.address = { ...billingAddress };
			}
			if (Object.keys(billing_details).length) {
				payload.billing_details = billing_details;
			}
			const { error, paymentMethod } = await this.stripe.createPaymentMethod(payload);
			if (error) {
				return { paymentMethodId: '', error: error.message };
			}
			return { paymentMethodId: paymentMethod.id };
		} catch (e: any) {
			return { paymentMethodId: '', error: e?.message || 'Failed to create payment method' };
		}
	}
}
